import { useMemo, useState } from "react";

import { MetricBlock } from "../api";
import PlayerCell from "../components/PlayerCell";
import ProgressBar from "../components/ProgressBar";
import { classFor } from "../data/classes";

interface SourceRow {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  byTarget: Record<string, number>;
}

interface TableRow {
  name?: string;
  type?: string;
  icon?: string;
  damage: number;
}

const fmt = (n: number) => n.toLocaleString("en-US");
const fmtCompact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : `${Math.round(n)}`;

/**
 * Per-player damage done to configured must-die NPCs (Spitfire Totem,
 * Water Elemental Totem, etc). Same tabbed shape as Engineering Damage:
 * "Total" tab sums across targets; each remaining tab is a single
 * target with players sorted by damage to that target.
 */
export default function PriorityTargetDamage({
  block,
}: {
  block: MetricBlock;
}) {
  const rows = (block.rows as unknown as SourceRow[]) ?? [];

  const targetSummary = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of rows) {
      for (const [target, dmg] of Object.entries(r.byTarget ?? {})) {
        out[target] = (out[target] ?? 0) + dmg;
      }
    }
    return out;
  }, [rows]);

  const targets = useMemo(
    () => Object.entries(targetSummary).sort((a, b) => b[1] - a[1]),
    [targetSummary],
  );

  const [selected, setSelected] = useState<"total" | string>("total");

  const raidTotal =
    (block.totals?.raidTotal as number | undefined) ??
    Object.values(targetSummary).reduce((a, b) => a + b, 0);

  const { tableRows, title, subtitle, totalDamage } = useMemo(() => {
    if (selected === "total") {
      const r = [...rows]
        .map((row) => ({
          name: row.name,
          type: row.type,
          icon: row.icon,
          damage: row.total,
        }))
        .filter((row) => row.damage > 0)
        .sort((a, b) => b.damage - a.damage);
      return {
        tableRows: r,
        title: "Total Priority Target Damage",
        subtitle: `${targets.length} ${targets.length === 1 ? "target" : "targets"} tracked`,
        totalDamage: raidTotal,
      };
    }
    const r = rows
      .map((row) => ({
        name: row.name,
        type: row.type,
        icon: row.icon,
        damage: row.byTarget[selected] ?? 0,
      }))
      .filter((row) => row.damage > 0)
      .sort((a, b) => b.damage - a.damage);
    return {
      tableRows: r,
      title: selected,
      subtitle: undefined,
      totalDamage: targetSummary[selected] ?? 0,
    };
  }, [selected, rows, targets, raidTotal, targetSummary]);

  if (!rows.length || !targets.length) {
    return (
      <p className="text-sm text-gray-500">
        No damage to priority targets recorded - or no targets are configured
        for this raid yet (check{" "}
        <code>backend/app/data/raids/&lt;raid&gt;.json</code>{" "}
        <code>priorityTargets</code>).
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <Tabs targets={targets} selected={selected} onSelect={setSelected} />

      <TargetTable
        title={title}
        subtitle={subtitle}
        totalDamage={totalDamage}
        rows={tableRows}
      />
    </div>
  );
}

/* ----------------------------- Tabs ----------------------------- */

function Tabs({
  targets,
  selected,
  onSelect,
}: {
  targets: [string, number][];
  selected: "total" | string;
  onSelect: (s: "total" | string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tab
        active={selected === "total"}
        onClick={() => onSelect("total")}
        label="Total"
      />
      {targets.map(([target, dmg]) => (
        <Tab
          key={target}
          active={selected === target}
          onClick={() => onSelect(target)}
          label={target}
          aside={fmtCompact(dmg)}
        />
      ))}
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
  aside,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  aside?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={aside ? `${label} · ${aside}` : label}
      className={[
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
        "transition-colors max-w-[16rem]",
        active
          ? "border-accent/40 bg-accent/15 text-accent"
          : "border-border/60 bg-bg/40 text-gray-300 hover:bg-white/5 hover:text-gray-100",
      ].join(" ")}
    >
      <span className="truncate">{label}</span>
      {aside && (
        <span className="text-[10px] tabular-nums text-gray-500 flex-shrink-0">
          {aside}
        </span>
      )}
    </button>
  );
}

/* ----------------------------- Table ----------------------------- */

function TargetTable({
  title,
  subtitle,
  totalDamage,
  rows,
}: {
  title: string;
  subtitle?: string;
  totalDamage: number;
  rows: TableRow[];
}) {
  if (!rows.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs text-gray-500 mt-1">No data.</p>
      </section>
    );
  }

  const top = rows[0]?.damage ?? 0;
  const rankPad = String(rows.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <header className="flex items-baseline justify-between gap-2 px-4 pt-3 pb-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="text-xs text-gray-500 flex items-baseline gap-3">
          {subtitle && <span>{subtitle}</span>}
          <span className="tabular-nums">
            <span className="text-gray-300 font-medium">{fmt(totalDamage)}</span>
            {" damage"}
          </span>
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th className="pb-2 pr-3 font-medium text-right">Damage</th>
              <th className="pb-2 pr-4 font-medium w-1/3">% of best</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = top ? (r.damage / top) * 100 : 0;
              const cls = classFor(r.type);
              const padded = String(i + 1).padStart(rankPad, "0");
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/5 transition-colors"
                >
                  <td className="py-1.5 pl-4 pr-3 text-gray-500 tabular-nums">
                    {padded}
                  </td>
                  <td className="py-1.5 pr-3 max-w-[16rem]">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium">
                    {fmt(r.damage)}
                  </td>
                  <td className="py-1.5 pr-4">
                    <ProgressBar pct={pct} color={cls.color} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
